class PostedProduct extends HTMLElement {
  connectedCallback() {
    this.variantInput = this.querySelector('[data-posted-variant-id]');
    this.planInputs = [...this.querySelectorAll('[data-posted-plan]')];
    this.recipientInputs = [...this.querySelectorAll('[data-posted-recipient-option]')];
    this.chooser = this.querySelector('[data-posted-plans-chooser]');
    this.changeButton = this.querySelector('[data-posted-change-plan]');
    this.summary = this.querySelector('[data-posted-summary]');
    this.continueButton = this.querySelector('[data-posted-continue]');
    this.details = this.querySelector('[data-posted-details]');
    this.closeDetails = this.querySelector('[data-posted-close-details]');
    this.anonymousInput = this.querySelector('[data-posted-anonymous]');
    this.form = this.querySelector('form.posted-product__form');
    this.addButton = this.querySelector('[data-posted-add-another]');
    this.addedList = this.querySelector('[data-posted-added]');
    this.alert = this.querySelector('[data-posted-alert]');
    this.letters = [];
    this.fromHome = new URLSearchParams(window.location.search).has('variant');
    this.bindEvents();
    this.syncPlan();
    this.syncRecipient();
    this.showChosenPlanOnly();
  }

  // Arrived from a plan on the homepage: show just that membership, land on it, and offer "Change plan".
  showChosenPlanOnly() {
    if (!this.fromHome || !this.chooser) return;
    this.chooser.hidden = true;
    if (this.changeButton) this.changeButton.hidden = false;
    if (!window.matchMedia('(max-width: 61.99rem)').matches) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    requestAnimationFrame(() => this.summary?.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' }));
  }

  bindEvents() {
    this.planInputs.forEach((input) => input.addEventListener('change', () => this.syncPlan()));
    this.recipientInputs.forEach((input) => input.addEventListener('change', () => this.syncRecipient()));
    this.changeButton?.addEventListener('click', () => {
      this.chooser.hidden = false;
      this.changeButton.hidden = true;
      this.chooser.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
    this.continueButton?.addEventListener('click', () => this.openDetails());
    this.closeDetails?.addEventListener('click', () => this.hideDetails());
    this.addButton?.addEventListener('click', () => this.addAnother());
    this.addedList?.addEventListener('click', (event) => {
      const remove = event.target.closest('[data-remove-letter]');
      if (remove) this.removeLetter(Number(remove.dataset.removeLetter));
    });
    this.form?.addEventListener('input', (event) => {
      event.target.classList?.remove('is-invalid');
      event.target.removeAttribute?.('aria-invalid');
    });
    this.form?.addEventListener('submit', (event) => this.checkout(event));
  }

  isGift() {
    return this.recipientInputs.find((input) => input.checked)?.value === 'Someone else';
  }

  field(name) {
    return this.querySelector(`[name="properties[${name}]"]`);
  }

  // UAE mobile: 05x xxx xxxx, +971 5x xxx xxxx or 00971 5x xxx xxxx (spaces and dashes ignored).
  static isUaeMobile(value) {
    return /^(?:(?:\+|00)?971|0)?5\d{8}$/.test(value.replace(/[\s\-()]/g, ''));
  }

  showAlert(message) {
    if (!this.alert) return;
    this.alert.textContent = message;
    this.alert.hidden = !message;
  }

  // Marks every problem field and returns true when the letter is complete.
  validate() {
    const fields = [...this.details.querySelectorAll('input, select, textarea')].filter(
      (el) => !el.disabled && el.type !== 'checkbox' && !el.closest('[hidden]'),
    );
    let mobileProblem = false;
    let firstBad = null;
    fields.forEach((el) => {
      el.classList.remove('is-invalid');
      el.removeAttribute('aria-invalid');
      const value = el.value.trim();
      let ok = !el.required || value !== '';
      if (ok && value && (el.type === 'email' || el.type === 'url')) ok = el.checkValidity();
      if (ok && value && el.hasAttribute('data-posted-mobile') && !PostedProduct.isUaeMobile(value)) {
        ok = false;
        mobileProblem = true;
      }
      if (!ok) {
        el.classList.add('is-invalid');
        el.setAttribute('aria-invalid', 'true');
        firstBad ||= el;
      }
    });
    if (firstBad) {
      this.showAlert(mobileProblem ? this.dataset.tMobile : this.dataset.tRequired);
      firstBad.focus({ preventScroll: false });
      return false;
    }
    this.showAlert('');
    return true;
  }

  // One letter = one cart line: plan + who it is for + where it goes.
  buildLetter() {
    const value = (name) => (this.field(name)?.value || '').trim();
    const gift = this.isGift();
    const sender = value('Sender display name');
    const senderMobile = value('Sender mobile');
    const properties = {
      'Sender display name': sender,
      'Sender email': value('Sender email'),
      'Sender mobile': senderMobile,
      'Sender visibility': gift && this.anonymousInput?.checked ? 'Anonymous' : 'Known',
      'Purchase for': gift ? 'Someone else' : 'Myself',
      'Recipient name': gift ? value('Recipient name') : sender,
      'Recipient mobile': gift ? value('Recipient mobile') : senderMobile,
      Emirate: value('Emirate'),
      'Full delivery address': value('Full delivery address'),
      'Google Maps link': value('Google Maps link'),
      'Delivery notes': value('Delivery notes'),
      'First envelope message': value('First envelope message'),
    };
    Object.keys(properties).forEach((key) => {
      if (!properties[key]) delete properties[key];
    });
    const plan = this.planInputs.find((input) => input.checked);
    return {
      item: { id: Number(this.variantInput.value), quantity: 1, properties },
      title: plan?.dataset.title || '',
      who: gift ? properties['Recipient name'] : null,
    };
  }

  // Keep this letter, clear the recipient fields, and let the buyer add the next one.
  addAnother() {
    if (!this.validate()) return;
    this.letters.push(this.buildLetter());
    ['Recipient name', 'Recipient mobile', 'Emirate', 'Full delivery address', 'Google Maps link', 'Delivery notes', 'First envelope message'].forEach((name) => {
      const el = this.field(name);
      if (el) el.value = '';
    });
    this.renderLetters();
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    (this.chooser.hidden ? this.summary : this.chooser)?.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
  }

  removeLetter(index) {
    this.letters.splice(index, 1);
    this.renderLetters();
  }

  renderLetters() {
    if (!this.addedList) return;
    const { tSelf, tFor, tRemove } = this.dataset;
    this.addedList.replaceChildren(
      ...this.letters.map((letter, index) => {
        const li = document.createElement('li');
        const text = document.createElement('span');
        text.textContent = `${letter.title} · ${letter.who ? `${tFor} ${letter.who}` : tSelf}`;
        const button = document.createElement('button');
        button.type = 'button';
        button.dataset.removeLetter = String(index);
        button.textContent = tRemove;
        li.append(text, button);
        return li;
      }),
    );
    this.addedList.hidden = this.letters.length === 0;
  }

  // Add every letter to the bag, then go straight to checkout (no bag page).
  async checkout(event) {
    const button = this.querySelector('[data-posted-submit]');
    if (!this.form || !button || button.disabled) return;
    event.preventDefault();
    if (!this.validate()) return;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    const root = window.Shopify?.routes?.root || '/';
    const post = (path, body) =>
      fetch(root + path, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
    try {
      const current = this.buildLetter();
      const letters = [...this.letters, current];
      // The bag is rebuilt from this page so an old, abandoned bag can never double-charge.
      await post('cart/clear.js', {});
      const response = await post('cart/add.js', { items: letters.map((letter) => letter.item) });
      if (!response.ok) throw new Error('add failed');
      const sender = current.item.properties['Sender display name'] || '';
      await post('cart/update.js', { attributes: { Sender: sender, 'Letters in this order': String(letters.length) } });
      window.location.assign('/checkout');
    } catch (error) {
      button.disabled = false;
      button.removeAttribute('aria-busy');
      this.showAlert(this.dataset.tError);
    }
  }

  syncPlan() {
    const selected = this.planInputs.find((input) => input.checked);
    if (!selected) return;
    this.variantInput.value = selected.value;
    this.querySelectorAll('.posted-product__plan').forEach((plan) => plan.classList.remove('is-selected'));
    const plan = selected.closest('.posted-product__plan');
    plan?.classList.add('is-selected');
    this.querySelectorAll('[data-summary-title]').forEach((node) => {
      node.textContent = selected.dataset.title;
    });
    const continueText = this.querySelector('[data-continue-text]');
    if (continueText) continueText.textContent = selected.dataset.buttonLabel;
    const price = plan?.querySelector('.posted-product__plan-price > strong');
    const summaryPrice = this.querySelector('[data-summary-price]');
    if (price && summaryPrice) summaryPrice.innerHTML = price.innerHTML;
  }

  // Delivery details are needed for every letter. Buying for yourself just skips the recipient name and mobile.
  syncRecipient() {
    const gift = this.isGift();
    this.querySelectorAll('.posted-product__recipient-card').forEach((card) => card.classList.remove('is-selected'));
    this.recipientInputs.find((input) => input.checked)?.closest('.posted-product__recipient-card')?.classList.add('is-selected');
    [
      ['[data-posted-recipient-name]', 'Recipient name'],
      ['[data-posted-recipient-mobile]', 'Recipient mobile'],
    ].forEach(([wrapSelector, name]) => {
      this.querySelector(wrapSelector)?.toggleAttribute('hidden', !gift);
      const input = this.field(name);
      if (input) {
        input.disabled = !gift;
        input.required = gift;
      }
    });
    this.querySelector('[data-posted-anon-wrap]')?.toggleAttribute('hidden', !gift);
  }

  openDetails() {
    if (!this.details) return;
    this.details.hidden = false;
    this.continueButton?.setAttribute('aria-expanded', 'true');
    this.syncRecipient();
    this.details.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
    this.details.querySelector('input:not([type="radio"]):not([type="checkbox"]), select, textarea')?.focus({ preventScroll: true });
  }

  hideDetails() {
    if (!this.details) return;
    this.details.hidden = true;
    this.continueButton?.setAttribute('aria-expanded', 'false');
    this.continueButton?.focus();
  }
}

if (!customElements.get('posted-product')) customElements.define('posted-product', PostedProduct);
